import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { MdLocalShipping } from 'react-icons/md';
import SendVisualDesignerToSupplierModal from '../../modules/docs/components/SendVisualDesignerToSupplierModal';
import { documentsService } from '../../modules/docs/services/documents.service';
import {
  visualDesignerSurveysService,
  type VisualDesignerSurvey,
} from '../../modules/docs/services/visualDesignerSurveys.service';
import type { DocumentLineInput } from '../../modules/docs/types/documents.types';
import type { Customer } from '../../services/customerService';
import type { Project } from '../../types/project';
import { type ProductionDateEntry, type WorkflowTask } from '../../components/ProjectWorkflow';
import { directSupabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/supabase';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import OrderAcceptanceCard from './OrderAcceptanceCard';
import SupplierOrdersCard from './SupplierOrdersCard';
import { finalSurveyOpenForProject, primarySurvey } from './workspaceQuote.helpers';

type SupplierRow = Database['public']['Tables']['suppliers']['Row'];

interface SupplierStagePanelProps {
  project: Project;
  customer: Customer | null;
  tasks: WorkflowTask[];
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
  onProductionDatesChange?: (dates: ProductionDateEntry[]) => void;
  saving?: boolean;
  /** The checklist's Order Goods step, recorded here where the ordering happens. */
  orderPlaced?: boolean;
  onOrderPlacedChange?: (placed: boolean) => void;
}

function chosenSupplierRecord(supplier: Pick<SupplierRow, 'id' | 'supplier_name'>) {
  return {
    supplier_id: supplier.id,
    supplier_name: supplier.supplier_name,
    price: null,
    supplier_price_date_obtained: null,
    quote_reference: null,
  };
}

function selectIds(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter((id) => id.trim().length > 0);
  const single = String(value ?? '').trim();
  return single ? [single] : [];
}

type SurveyOption = {
  survey: VisualDesignerSurvey;
  itemCount: number;
  lines: DocumentLineInput[];
};

const SupplierStagePanel: React.FC<SupplierStagePanelProps> = ({
  project,
  customer,
  tasks,
  onProjectPatch,
  onProductionDatesChange,
  saving = false,
  orderPlaced = false,
  onOrderPlacedChange,
}) => {
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [surveys, setSurveys] = useState<SurveyOption[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [lifecycleRef, setLifecycleRef] = useState('');
  const [organisationId, setOrganisationId] = useState<string | null>(null);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  const [pendingPreferred, setPendingPreferred] = useState<string[] | null>(null);
  const [pendingChosen, setPendingChosen] = useState<string[] | null>(null);

  const loadSurveys = useCallback(async () => {
    setLoadingSurveys(true);
    setSurveyError(null);
    try {
      const [list, docs] = await Promise.all([
        visualDesignerSurveysService.listByProject(project.id),
        documentsService.getDocumentsByProject(project.id).catch(() => []),
      ]);
      const quote = docs.find((doc) => doc.kind === 'Quotation') || docs.find((doc) => doc.kind === 'Estimate') || null;
      setLifecycleRef(String(quote?.lifecycleRef || project.project_name || '').trim());
      setOrganisationId(quote?.organisationId || null);

      const finalOpen = finalSurveyOpenForProject({
        documents: docs,
        finalSurvey: primarySurvey(list, 'final'),
      });
      // The latest quote version, plus the Final Survey once it is open (kept on record
      // alongside it, and selected by default). Earlier quote versions are superseded.
      const finalSurvey = finalOpen ? primarySurvey(list, 'final') : null;
      const latestVersion = [...list].reverse().find((survey) => survey.kind !== 'final') || null;

      // A survey still bound to an Estimate version (the First Survey when the estimate was
      // progressed by hand) is kept on offer as the estimate record, labelled as such.
      const versionIds = list.map((survey) => survey.documentVersionId).filter((id): id is string => !!id);
      const { data: versionRows } = versionIds.length
        ? await directSupabase.from('document_versions').select('id, document_id').in('id', versionIds)
        : { data: [] };
      const estimateIds = new Set(docs.filter((doc) => doc.kind === 'Estimate').map((doc) => doc.id));
      const estimateVersionIds = new Set(
        (versionRows || []).filter((row) => estimateIds.has(String(row.document_id))).map((row) => String(row.id)),
      );
      const isEstimate = (survey: VisualDesignerSurvey) => (
        !!survey.documentVersionId && estimateVersionIds.has(survey.documentVersionId)
      );
      const firstSurvey = primarySurvey(list, 'first');
      const estimateRecord = firstSurvey && firstSurvey.id !== latestVersion?.id && isEstimate(firstSurvey)
        ? firstSurvey
        : null;
      const offered = [estimateRecord, latestVersion, finalSurvey]
        .filter((survey): survey is NonNullable<typeof survey> => !!survey);

      const next: SurveyOption[] = [];
      for (const survey of offered) {
        const lines = await visualDesignerSurveysService.listItems(survey.id);
        next.push({
          survey: isEstimate(survey) ? { ...survey, label: 'Estimate' } : survey,
          lines,
          itemCount: lines.length,
        });
      }
      const ordered = [...next].reverse();
      setSurveys(ordered);
      setSelectedSurveyId((current) => (
        current && ordered.some((item) => item.survey.id === current) ? current : ordered[0]?.survey.id || ''
      ));
    } catch (err) {
      setSurveyError(err instanceof Error ? err.message : 'Failed to load Visual Designer surveys');
      setSurveys([]);
    } finally {
      setLoadingSurveys(false);
    }
  }, [project.id, project.project_name]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await directSupabase.from('suppliers').select('*').order('supplier_name');
      if (!cancelled) setSuppliers(data || []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys]);

  const selected = surveys.find((item) => item.survey.id === selectedSurveyId) || null;
  const storedPreferredIds = project.preferred_suppliers || [];
  const preferredIds = pendingPreferred ?? storedPreferredIds;
  const preferredRows = useMemo(
    () => preferredIds
      .map((id) => suppliers.find((row) => row.id === id))
      .filter((row): row is SupplierRow => !!row),
    [preferredIds, suppliers],
  );
  const prices = Array.isArray(project.supplier_prices) ? project.supplier_prices : [];
  const storedChosenIds = prices
    .map((row) => row.supplier_id)
    .filter((id) => preferredIds.includes(id));
  const chosenIds = pendingChosen ?? storedChosenIds;
  const preferredSupplierId = chosenIds[0] || preferredIds[0] || null;
  const productionTask = tasks.find((task) => task.task === 'Production Date');
  const productionDates = productionTask?.productionDates || [];
  const customerName = String(customer?.name || project.customer_name || 'Customer').trim();
  const canSend = !!selected && selected.itemCount > 0;

  const persistSuppliers = async (patch: Partial<Project>) => {
    setSupplierError(null);
    try {
      await onProjectPatch(patch);
    } catch (err) {
      setSupplierError(err instanceof Error ? err.message : 'Failed to update suppliers');
      throw err;
    }
  };

  const recordsFor = (ids: string[]) => ids
    .map((id) => suppliers.find((row) => row.id === id))
    .filter((row): row is SupplierRow => !!row)
    .map(chosenSupplierRecord);

  const handlePreferredChange = async (nextIds: string[]) => {
    if (supplierSaving) return;
    const nextChosen = chosenIds.filter((id) => nextIds.includes(id));
    setSupplierSaving(true);
    setPendingPreferred(nextIds);
    if (nextChosen.length !== chosenIds.length) setPendingChosen(nextChosen);
    try {
      await persistSuppliers({
        preferred_suppliers: nextIds,
        ...(nextChosen.length !== chosenIds.length ? { supplier_prices: recordsFor(nextChosen) } : {}),
      });
    } catch {
      setPendingPreferred(null);
      setPendingChosen(null);
    } finally {
      setPendingPreferred(null);
      setPendingChosen(null);
      setSupplierSaving(false);
    }
  };

  const handleChosenChange = async (nextIds: string[]) => {
    if (supplierSaving) return;
    setSupplierSaving(true);
    setPendingChosen(nextIds);
    try {
      await persistSuppliers({ supplier_prices: recordsFor(nextIds) });
    } catch {
      setPendingChosen(null);
    } finally {
      setPendingChosen(null);
      setSupplierSaving(false);
    }
  };

  return (
    <Stack gap={2}>
      {onOrderPlacedChange && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <div>
                <Typography level="title-md">Order</Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  {orderPlaced
                    ? 'Goods ordered. Add the production date below once the supplier confirms it.'
                    : 'Mark the order as placed once it has gone to the supplier.'}
                </Typography>
              </div>
              <Checkbox
                label="Order placed"
                checked={orderPlaced}
                disabled={saving}
                onChange={(event) => onOrderPlacedChange(event.target.checked)}
              />
            </Stack>
          </CardContent>
        </Card>
      )}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 0.5 }}>Preferred suppliers</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.5 }}>
            Choose who you would consider for this job, then mark one or more as the chosen suppliers.
          </Typography>
          {supplierError && <Alert color="danger" sx={{ mb: 1.25 }}>{supplierError}</Alert>}
          <Stack gap={1.5}>
            <FormControl>
              <FormLabel>Preferred</FormLabel>
              <Select
                multiple
                placeholder="Select preferred suppliers..."
                value={preferredIds}
                disabled={suppliers.length === 0}
                onChange={(_, value) => {
                  void handlePreferredChange(selectIds(value));
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(Array.isArray(selected) ? selected : []).map((item) => {
                      const id = typeof item === 'string' ? item : String((item as { value?: string }).value || '');
                      const supplier = suppliers.find((row) => row.id === id);
                      return (
                        <Chip key={id} size="sm" variant="soft" color="primary">
                          {supplier?.supplier_name || id}
                        </Chip>
                      );
                    })}
                  </Box>
                )}
                slotProps={{ listbox: { sx: { maxHeight: 240, overflow: 'auto' } } }}
              >
                {suppliers.map((supplier) => (
                  <Option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</Option>
                ))}
              </Select>
              <FormHelperText>You can select more than one preferred supplier.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Chosen suppliers</FormLabel>
              <Select
                multiple
                placeholder={preferredRows.length === 0 ? 'Add a preferred supplier first' : 'Select chosen suppliers...'}
                value={chosenIds}
                disabled={preferredRows.length === 0}
                onChange={(_, value) => {
                  void handleChosenChange(selectIds(value));
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(Array.isArray(selected) ? selected : []).map((item) => {
                      const id = typeof item === 'string' ? item : String((item as { value?: string }).value || '');
                      const supplier = suppliers.find((row) => row.id === id);
                      return (
                        <Chip key={id} size="sm" variant="soft" color="success">
                          {supplier?.supplier_name || id}
                        </Chip>
                      );
                    })}
                  </Box>
                )}
                slotProps={{ listbox: { sx: { maxHeight: 240, overflow: 'auto' } } }}
              >
                {preferredRows.map((supplier) => (
                  <Option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</Option>
                ))}
              </Select>
              <FormHelperText>You can select more than one chosen supplier for this job.</FormHelperText>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <WorkspaceCardHeader
            icon={MdLocalShipping}
            title="Send survey to supplier(s)"
            description="Send a Visual Designer survey to a window supplier using the window-order email template."
          />
          {surveyError && <Alert color="danger" sx={{ mb: 1.25 }}>{surveyError}</Alert>}
          {loadingSurveys ? (
            <CircularProgress size="sm" />
          ) : surveys.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              No Visual Designer surveys yet. Add window designs on Quotes / Estimates first.
            </Typography>
          ) : (
            <Stack gap={1}>
              {surveys.map((item) => {
                const active = item.survey.id === selectedSurveyId;
                return (
                  <Box
                    key={item.survey.id}
                    onClick={() => setSelectedSurveyId(item.survey.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedSurveyId(item.survey.id);
                      }
                    }}
                    sx={{
                      p: 1.25,
                      border: '1px solid',
                      borderColor: active ? 'var(--jarvis-selection-bg)' : 'divider',
                      bgcolor: active ? 'var(--jarvis-selection-bg)' : 'transparent',
                      color: active ? 'var(--jarvis-selection-text)' : 'inherit',
                      borderRadius: 'sm',
                      cursor: 'pointer',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                      <Box>
                        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                          <Typography level="body-sm" fontWeight="lg" sx={{ color: 'inherit' }}>
                            {item.survey.label}
                          </Typography>
                        </Stack>
                        <Typography
                          level="body-xs"
                          sx={{ color: active ? 'inherit' : 'neutral.500', opacity: active ? 0.85 : 1 }}
                        >
                          {item.itemCount === 1 ? '1 item' : `${item.itemCount} items`}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  disabled={!canSend}
                  onClick={() => setSendOpen(true)}
                >
                  Send to supplier
                </Button>
              </Stack>
              {selected && selected.itemCount === 0 && (
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  This survey has no window designs to send.
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <OrderAcceptanceCard project={project} onProjectPatch={onProjectPatch} />

      <SupplierOrdersCard
        projectId={project.id}
        suppliers={suppliers}
        preferredSupplierIds={preferredIds}
        chosenSupplierIds={chosenIds}
        productionDates={productionDates}
        onProductionDatesChange={(dates) => onProductionDatesChange?.(dates)}
        productTypes={project.product_types}
        readOnly={saving}
      />

      {project.notes && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>Special detail</Typography>
            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{project.notes}</Typography>
          </CardContent>
        </Card>
      )}

      {selected && (
        <SendVisualDesignerToSupplierModal
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          projectId={project.id}
          surveyId={selected.survey.id}
          surveyLabel={selected.survey.label}
          documentVersionId={selected.survey.documentVersionId}
          sourceLines={selected.lines.map((line, index) => ({
            id: `${selected.survey.id}-${index}`,
            name: line.name,
            qty: line.qty,
            materialId: line.materialId ?? null,
            metaJson: line.metaJson,
          }))}
          customerReference={lifecycleRef || selected.survey.label}
          customerName={customerName}
          organisationId={organisationId}
          preferredSupplierId={preferredSupplierId}
        />
      )}
    </Stack>
  );
};

export default SupplierStagePanel;
